<script setup lang="ts">
import { EButton } from '@erpx/ui'

import AppIcon from '@/components/AppIcon.vue'
import { sessionExpired, tenantSuspended } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'

/**
 * Persistent state banners.
 *
 * A suspended workspace (§10) is not an error the user can fix by retrying, so
 * it gets a banner rather than a toast: it stays on screen, explains why the
 * buttons stopped working, and does not time out mid-sentence.
 */

const auth = useAuthStore()
const router = useRouter()

/**
 * Not awaited: `auth.logout()` ends the session locally before its first
 * await, and a revoke that hangs must not keep this screen up (see
 * `UserMenu.vue`). `replace`, so the previous person's URL is not one Back
 * press away.
 */
function signOut(): void {
  void auth.logout()
  void router.replace({ name: 'login' })
}
</script>

<template>
  <div v-if="tenantSuspended" class="banner banner--danger" role="alert">
    <AppIcon name="alert" :size="15" />
    <span>{{ $t('shell.banner.suspended') }}</span>
  </div>

  <div v-else-if="sessionExpired" class="banner" role="status">
    <AppIcon name="clock" :size="15" />
    <span>{{ $t('shell.banner.sessionEnded') }}</span>
    <EButton variant="secondary" size="sm" @click="signOut">{{ $t('common.actions.signIn') }}</EButton>
  </div>
</template>
